import { useState, useRef, useLayoutEffect } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import type { DbMessage } from "../lib/api";

/** DB messages → AI SDK UI messages (server is the source of truth — I10). */
function toUiMessages(msgs: DbMessage[]): UIMessage[] {
  return msgs.map((m) => ({
    id: m.id,
    role: m.role,
    parts: [{ type: "text", text: m.content }],
  }));
}

function textOf(m: UIMessage): string {
  return (m.parts ?? [])
    .filter((p): p is { type: "text"; text: string } => p.type === "text")
    .map((p) => p.text)
    .join("");
}

/** F2/UC1 — streaming interview chat over the AI SDK data-stream transport. */
export function Chat({
  sessionId,
  initialMessages,
}: {
  sessionId: string;
  initialMessages: DbMessage[];
}) {
  const [input, setInput] = useState("");
  const taRef = useRef<HTMLTextAreaElement>(null);
  const { messages, sendMessage, status } = useChat({
    id: sessionId,
    messages: toUiMessages(initialMessages),
    transport: new DefaultChatTransport({ api: `/api/chat/${sessionId}` }),
  });

  const busy = status === "submitted" || status === "streaming";

  // Auto-grow the textarea to its content (capped via CSS max-height).
  useLayoutEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [input]);

  function send() {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    sendMessage({ text });
  }

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    send();
  }

  // Enter sends; Shift+Enter inserts a newline. Suppressed during IME composition.
  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div data-testid="log" className="flex-1 space-y-2 overflow-y-auto p-4">
        {messages.map((m) => (
          <div
            key={m.id}
            className={
              "max-w-[80%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm " +
              (m.role === "user"
                ? "ml-auto bg-blue-600 text-white"
                : "mr-auto bg-neutral-200 dark:bg-neutral-800")
            }
          >
            {textOf(m)}
          </div>
        ))}
        {busy && <div className="mr-auto animate-pulse text-sm text-neutral-400">…</div>}
      </div>
      <form onSubmit={submit} className="flex gap-2 border-t border-neutral-200 p-3 dark:border-neutral-800">
        <textarea
          ref={taRef}
          data-testid="message"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder="Antworten… (Enter senden · Shift+Enter neue Zeile)"
          className="max-h-40 flex-1 resize-none rounded-md border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-neutral-700 dark:bg-neutral-800"
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Senden
        </button>
      </form>
    </div>
  );
}
